import { useState, useEffect, useMemo, useRef } from "react";
import {
  createVenue, updateVenue, deleteVenue, 
  getVenueLocations, createVenueLocation, updateVenueLocation, deleteVenueLocation,
  getClients, extractErrorMessage, importVenuesFromExcel 
} from "../lib/api";
// EVENTSFLO-ADMIN-VENUE-SHARE-COPY-UI-V1
import {
  VenueVisibilityBadge,
  VenueShareDialog,
  VenueCopyToClientDialog,
} from "../components/venues/VenueShareCopyDialogs";
import {
  PageHeader,
  DataTable,
  LoadingSpinner,
  EmptyState,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { CountrySelect } from "../components/shared/CountrySelect";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, MapPin, Pencil, Trash2, Search, ChevronRight, ChevronDown, Building2, Upload, AlertTriangle, CheckCircle, XCircle, Loader2, AlertCircle, Share2, Copy } from "lucide-react";
// EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1
import { GuideButton } from "../components/common/GuideButton";

// Admin mode check - in production this would come from auth context
const isAdminUser = () => {
  // Check localStorage for admin mode (can be set via console: localStorage.setItem('eventsflo-admin', 'true'))
  return localStorage.getItem('eventsflo-admin') === 'true';
};

export default function VenuesPage() {
  const { formatDate } = useFormatters();
  
  // Main data state
  const [venues, setVenues] = useState([]);
  const [venueLocations, setVenueLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [selectedVenueLocation, setSelectedVenueLocation] = useState(null);
  const [expandedVenues, setExpandedVenues] = useState(new Set()); // Track expanded venues
  
  // Dialog state
  const [venueDialogOpen, setVenueDialogOpen] = useState(false);
  const [venueLocationDialogOpen, setVenueLocationDialogOpen] = useState(false);
  const [deleteVenueDialogOpen, setDeleteVenueDialogOpen] = useState(false);
  const [deleteVenueLocationDialogOpen, setDeleteVenueLocationDialogOpen] = useState(false);
  // EVENTSFLO-ADMIN-VENUE-SHARE-COPY-UI-V1
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  // clientsById lookup for badge labels
  const clientsById = useMemo(() => {
    const m = {};
    (clients || []).forEach((c) => { m[c.id] = c; });
    return m;
  }, [clients]);
  
  // Import state
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const [importResultOpen, setImportResultOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Form data for Venue
  const [venueFormData, setVenueFormData] = useState({
    client_id: "",
    name: "",
    address: "",
    address_line_2: "",
    address_line_3: "",
    city: "",
    postcode: "",
    country: "",
    notes: "",
  });
  
  // Form data for VenueLocation
  const [venueLocationFormData, setVenueLocationFormData] = useState({
    venue_id: "",
    name: "",
    desc_short: "",
    description: "",
    address1: "",
    address2: "",
    city: "",
    postcode: "",
    country: "",
    latitude: "",
    longitude: "",
    contact_name1: "",
    contact_phone1: "",
    contact_email1: "",
  });

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Server-side state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const PAGE_SIZE = 100;

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadData(1);
    }, searchTerm ? 300 : 0);
    return () => clearTimeout(timer);
  }, [searchTerm, sortField, sortDirection]);

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Use unified /api/v2/venues endpoint
  const loadData = async (page = 1, append = false) => {
    try {
      if (!append) {
        setLoading(true);
      }
      
      // Build query params for unified endpoint
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', PAGE_SIZE.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      params.append('sort', `${sortField}:${sortDirection}`);
      
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('eventsflo_auth_token');
      
      const [venuesRes, venueLocationsRes, clientRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/venues?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.json()),
        page === 1 ? getVenueLocations() : Promise.resolve({ data: venueLocations }),
        page === 1 ? getClients() : Promise.resolve({ data: clients }),
      ]);
      
      // Handle unified response format
      const venuesData = venuesRes.data || [];
      const meta = venuesRes.meta || { page: 1, limit: PAGE_SIZE, total: venuesData.length, total_pages: 1 };
      
      if (append) {
        setVenues(prev => [...prev, ...venuesData]);
      } else {
        setVenues(venuesData);
      }
      
      setTotalCount(meta.total);
      setHasMore(meta.page < meta.total_pages);
      setCurrentPage(meta.page);
      
      if (page === 1) {
        setVenueLocations(venueLocationsRes.data);
        setClients(clientRes.data);
      }
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Server-side sort handler
  const handleServerSort = (field, direction) => {
    if (direction === null) {
      setSortField("name");
      setSortDirection("asc");
    } else {
      setSortField(field);
      setSortDirection(direction);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || "Unknown";
  };
  
  // Get venue locations for a specific venue (sorted A-Z)
  const getVenueLocationsForVenue = (venueId) => {
    return venueLocations
      .filter(vl => vl.venue_id === venueId)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: 'base' }));
  };
  
  // Count venue locations for a venue
  const getVenueLocationCount = (venueId) => {
    return venueLocations.filter(vl => vl.venue_id === venueId).length;
  };

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Server-side filtering handles venue search
  // Local filtering only for venue location matching (to support auto-expand feature)
  const autoExpandIds = useMemo(() => {
    if (!searchTerm.trim()) {
      return new Set();
    }
    
    const term = searchTerm.toLowerCase();
    const venuesWithLocationMatch = new Set();
    
    // Check if any venue locations match the search term
    venues.forEach(venue => {
      const venueLocationsList = venueLocations.filter(vl => vl.venue_id === venue.id);
      const hasMatchingLocation = venueLocationsList.some(vl => 
        (vl.name || "").toLowerCase().includes(term) ||
        (vl.desc_short || "").toLowerCase().includes(term) ||
        (vl.address1 || "").toLowerCase().includes(term)
      );
      
      if (hasMatchingLocation) {
        venuesWithLocationMatch.add(venue.id);
      }
    });
    
    return venuesWithLocationMatch;
  }, [venues, venueLocations, searchTerm]);
  
  // Toggle venue expansion
  const toggleVenueExpanded = (venueId) => {
    setExpandedVenues(prev => {
      const next = new Set(prev);
      if (next.has(venueId)) {
        next.delete(venueId);
      } else {
        next.add(venueId);
      }
      return next;
    });
  };
  
  // Check if a venue should show its locations (expanded manually OR auto-expanded from search)
  const isVenueExpanded = (venueId) => {
    return expandedVenues.has(venueId) || autoExpandIds.has(venueId);
  };
  
  // Clear search and collapse all
  const handleClearSearch = () => {
    setSearchTerm("");
    setExpandedVenues(new Set());
  };

  // ============== VENUE CRUD ==============
  
  const handleOpenVenueDialog = (venue = null) => {
    if (venue) {
      setVenueFormData({
        client_id: venue.client_id || "",
        name: venue.name || "",
        address: venue.address || "",
        address_line_2: venue.address_line_2 || "",
        address_line_3: venue.address_line_3 || "",
        city: venue.city || "",
        postcode: venue.postcode || "",
        country: venue.country || "",
        notes: venue.notes || "",
      });
      setSelectedVenue(venue);
    } else {
      setVenueFormData({
        client_id: "",
        name: "",
        address: "",
        address_line_2: "",
        address_line_3: "",
        city: "",
        postcode: "",
        country: "",
        notes: "",
      });
      setSelectedVenue(null);
    }
    setVenueDialogOpen(true);
  };

  const handleVenueSave = async () => {
    try {
      // Only name and address are required - client is OPTIONAL
      if (!venueFormData.name || !venueFormData.address) {
        toast.error("Please fill in all required fields (Name and Address)");
        return;
      }

      // Build payload - exclude empty client_id to allow independent venues
      const payload = { ...venueFormData };
      if (!payload.client_id) {
        delete payload.client_id; // Don't send empty string
      }

      if (selectedVenue) {
        await updateVenue(selectedVenue.id, payload);
        toast.success("Venue updated successfully");
      } else {
        await createVenue(payload);
        toast.success("Venue created successfully");
      }
      setVenueDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to save venue");
    }
  };

  const handleDeleteVenueConfirm = async () => {
    try {
      await deleteVenue(selectedVenue.id);
      toast.success("Venue deleted successfully");
      setDeleteVenueDialogOpen(false);
      setSelectedVenue(null);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to delete venue");
    }
  };

  // ============== VENUE LOCATION CRUD ==============
  
  const handleOpenVenueLocationDialog = (venueLocation = null, venueId = null) => {
    if (venueLocation) {
      setVenueLocationFormData({
        venue_id: venueLocation.venue_id || "",
        name: venueLocation.name || "",
        desc_short: venueLocation.desc_short || "",
        description: venueLocation.description || "",
        address1: venueLocation.address1 || "",
        address2: venueLocation.address2 || "",
        city: venueLocation.city || "",
        postcode: venueLocation.postcode || "",
        country: venueLocation.country || "",
        latitude: venueLocation.latitude?.toString() || "",
        longitude: venueLocation.longitude?.toString() || "",
        contact_name1: venueLocation.contact_name1 || "",
        contact_phone1: venueLocation.contact_phone1 || "",
        contact_email1: venueLocation.contact_email1 || "",
      });
      setSelectedVenueLocation(venueLocation);
    } else {
      // When creating new location, auto-populate from venue if venueId provided
      let initialData = {
        venue_id: venueId || "",
        name: "",
        desc_short: "",
        description: "",
        address1: "",
        address2: "",
        city: "",
        postcode: "",
        country: "",
        latitude: "",
        longitude: "",
        contact_name1: "",
        contact_phone1: "",
        contact_email1: "",
      };
      
      // Auto-populate address from venue
      // STABILIZE_OOT_TRAVEL_ONLY_V1: Fixed to use correct venue field names  
      if (venueId) {
        const venue = venues.find(v => v.id === venueId);
        if (venue) {
          initialData = {
            ...initialData,
            // Map venue.address to venueLocation.address1
            address1: venue.address || "",
            address2: venue.address_line_2 || "",
            city: venue.city || "",
            postcode: venue.postcode || "",
            country: venue.country || "",
          };
        }
      }
      
      setVenueLocationFormData(initialData);
      setSelectedVenueLocation(null);
    }
    setVenueLocationDialogOpen(true);
  };
  
  // Auto-populate address fields when venue selection changes
  // STABILIZE_OOT_TRAVEL_ONLY_V1: Fixed to use correct venue field names
  const handleVenueSelectionChange = (venueId) => {
    const venue = venues.find(v => v.id === venueId);
    if (venue) {
      setVenueLocationFormData(prev => ({
        ...prev,
        venue_id: venueId,
        // Map venue.address to venueLocation.address1
        address1: prev.address1 || venue.address || "",
        address2: prev.address2 || venue.address_line_2 || "",
        city: prev.city || venue.city || "",
        postcode: prev.postcode || venue.postcode || "",
        country: prev.country || venue.country || "",
      }));
    } else {
      setVenueLocationFormData(prev => ({ ...prev, venue_id: venueId }));
    }
  };

  const handleVenueLocationSave = async () => {
    try {
      if (!venueLocationFormData.venue_id || !venueLocationFormData.name) {
        toast.error("Please fill in all required fields");
        return;
      }

      const submitData = {
        ...venueLocationFormData,
        latitude: venueLocationFormData.latitude ? parseFloat(venueLocationFormData.latitude) : null,
        longitude: venueLocationFormData.longitude ? parseFloat(venueLocationFormData.longitude) : null,
      };

      if (selectedVenueLocation) {
        await updateVenueLocation(selectedVenueLocation.id, submitData);
        toast.success("Location updated successfully");
      } else {
        await createVenueLocation(submitData);
        toast.success("Location created successfully");
      }
      setVenueLocationDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to save location");
    }
  };

  const handleDeleteVenueLocationConfirm = async () => {
    try {
      await deleteVenueLocation(selectedVenueLocation.id);
      toast.success("Location deleted successfully");
      setDeleteVenueLocationDialogOpen(false);
      setSelectedVenueLocation(null);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to delete location");
    }
  };

  // ============== IMPORT HANDLERS ==============
  
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Strict .xlsx validation
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error("Only .xlsx files are supported.");
      e.target.value = '';
      return;
    }
    
    setSelectedFile(file);
    setImportConfirmOpen(true);
    e.target.value = ''; // Reset input for future selections
  };
  
  const handleImportConfirm = async () => {
    if (!selectedFile || importLoading) return;
    
    // Get first client for import (or could prompt user)
    const clientId = clients[0]?.id;
    if (!clientId) {
      toast.error("No clients available. Create a client first.");
      setImportConfirmOpen(false);
      setSelectedFile(null);
      return;
    }
    
    // Admin API key - in production this would come from auth context
    const adminApiKey = "ef-admin-2026-secure-key";
    
    setImportLoading(true);
    setImportConfirmOpen(false);
    
    try {
      const response = await importVenuesFromExcel(selectedFile, clientId, adminApiKey);
      setImportResult(response.data);
      setImportResultOpen(true);
      
      // Only show success toast if no errors
      if (response.data.success && (!response.data.errors || response.data.errors.length === 0)) {
        toast.success(`Import complete: ${response.data.venues_created} venues, ${response.data.locations_created} locations`);
      } else if (response.data.success && response.data.errors?.length > 0) {
        toast.warning(`Import completed with ${response.data.errors.length} error(s)`);
      }
      
      loadData(); // Refresh venue list
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'object' 
        ? errorDetail.message || JSON.stringify(errorDetail)
        : errorDetail || extractErrorMessage(err) || "Import failed";
      
      setImportResult({
        success: false,
        venues_created: 0,
        locations_created: 0,
        errors: [errorMsg],
        warnings: [],
        rows_processed: 0
      });
      setImportResultOpen(true);
      toast.error("Import failed");
    } finally {
      // Complete state cleanup
      setImportLoading(false);
      setSelectedFile(null);
    }
  };
  
  const handleImportCancel = () => {
    setImportConfirmOpen(false);
    setSelectedFile(null);
  };

  // Venue columns for the table
  const venueColumns = [
    { 
      key: "name", 
      label: "Venue Name",
      render: (_, venue) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{venue.name}</span>
        </div>
      )
    },
    { 
      key: "client_id", 
      label: "Client",
      render: (_, venue) => getClientName(venue.client_id)
    },
    { 
      key: "address", 
      label: "Address",
      render: (_, venue) => (
        <span className="text-muted-foreground">
          {[venue.address, venue.city, venue.postcode].filter(Boolean).join(", ")}
        </span>
      )
    },
    { 
      key: "locations_count", 
      label: "Locations",
      render: (_, venue) => {
        const count = getVenueLocationCount(venue.id);
        return (
          <span className={count > 0 ? "text-primary font-medium" : "text-muted-foreground"}>
            {count} {count === 1 ? 'location' : 'locations'}
          </span>
        );
      }
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, venue) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenVenueLocationDialog(null, venue.id);
            }}
            data-testid={`add-location-${venue.id}`}
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenVenueDialog(venue);
            }}
            data-testid={`edit-venue-${venue.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedVenue(venue);
              setDeleteVenueDialogOpen(true);
            }}
            data-testid={`delete-venue-${venue.id}`}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  // Check admin status for import visibility
  const showImportButton = isAdminUser();

  return (
    <div className="space-y-6" data-testid="venues-page">
      <PageHeader
        title="Venues"
        subtitle={`${venues.length} ${venues.length === 1 ? 'venue' : 'venues'} • ${venueLocations.length} ${venueLocations.length === 1 ? 'location' : 'locations'}`}
        actions={
          <div className="flex gap-2">
            {/* EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1 */}
            <GuideButton testId="venues-guide-btn" />
            {showImportButton && (
              <Button 
                variant="outline" 
                onClick={handleImportClick}
                disabled={importLoading}
                data-testid="import-venues-btn"
              >
                {importLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Import Excel
              </Button>
            )}
            <Button onClick={() => handleOpenVenueDialog()} data-testid="create-venue-btn">
              <Plus className="mr-2 h-4 w-4" /> New Venue
            </Button>
          </div>
        }
      />
      
      {/* Hidden file input for import (only rendered for admin) */}
      {showImportButton && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="import-file-input"
        />
      )}

      {/* Search */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search venues or locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="venue-search-input"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="clear-search-btn"
            >
              <XCircle className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Server-side filtering */}
      {venues.length === 0 ? (
        <EmptyState
          icon={MapPin}
          title="No venues found"
          description={searchTerm ? "Try adjusting your search" : "Create your first venue to get started"}
          action={
            !searchTerm && (
              <Button onClick={() => handleOpenVenueDialog()} data-testid="empty-create-venue-btn">
                <Plus className="mr-2 h-4 w-4" /> New Venue
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-2">
          {/* Pagination info */}
          <div className="text-sm text-muted-foreground mb-2">
            {venues.length} of {totalCount} venues
            {hasMore && <span className="text-xs ml-1">(more available)</span>}
          </div>
          
          {venues.map((venue) => {
            const locations = getVenueLocationsForVenue(venue.id);
            const locationCount = locations.length;
            const expanded = isVenueExpanded(venue.id);
            
            return (
              <div key={venue.id} className="border border-border rounded-lg overflow-hidden bg-card" data-testid={`venue-card-${venue.id}`}>
                {/* Venue Header Row - Clickable to expand/collapse */}
                <div 
                  className="flex items-center justify-between px-4 py-3 hover:bg-surface-elevated/20 cursor-pointer"
                  onClick={() => toggleVenueExpanded(venue.id)}
                  data-testid={`venue-row-${venue.id}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Expand/Collapse chevron */}
                    <button
                      className="p-0.5 hover:bg-surface-hover rounded transition-transform"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleVenueExpanded(venue.id);
                      }}
                      data-testid={`toggle-venue-${venue.id}`}
                    >
                      {expanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                    <Building2 className="h-5 w-5 text-primary" />
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">{venue.name}</h3>
                        {/* EVENTSFLO-ADMIN-VENUE-SHARE-COPY-UI-V1 */}
                        <VenueVisibilityBadge venue={venue} clientsById={clientsById} />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {venue.client_id ? getClientName(venue.client_id) : "No Client"} • {[
                          venue.address, 
                          venue.address_line_2, 
                          venue.address_line_3, 
                          venue.city, 
                          venue.postcode, 
                          venue.country
                        ].filter(Boolean).join(", ")}
                        {locationCount > 0 && (
                          <span className="ml-2 text-xs">({locationCount} {locationCount === 1 ? 'location' : 'locations'})</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {/* EVENTSFLO-ADMIN-VENUE-SHARE-COPY-UI-V1 */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedVenue(venue);
                        setShareDialogOpen(true);
                      }}
                      data-testid={`share-venue-btn-${venue.id}`}
                      title="Share with clients"
                    >
                      <Share2 className="h-4 w-4 mr-1" /> Share
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedVenue(venue);
                        setCopyDialogOpen(true);
                      }}
                      data-testid={`copy-venue-btn-${venue.id}`}
                      title="Copy to a client"
                    >
                      <Copy className="h-4 w-4 mr-1" /> Copy
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenVenueLocationDialog(null, venue.id)}
                      data-testid={`add-location-${venue.id}`}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Location
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenVenueDialog(venue)}
                      data-testid={`edit-venue-btn-${venue.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedVenue(venue);
                        setDeleteVenueDialogOpen(true);
                      }}
                      data-testid={`delete-venue-btn-${venue.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                
                {/* Venue Locations List - Only shown when expanded */}
                {expanded && locationCount > 0 && (
                  <div className="divide-y divide-border border-t border-border">
                    {locations.map((vl) => (
                      <div key={vl.id} className="flex items-center justify-between px-4 py-2 pl-14 hover:bg-surface-elevated/20" data-testid={`location-row-${vl.id}`}>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{vl.name}</span>
                          {vl.desc_short && (
                            <span className="text-sm text-muted-foreground">- {vl.desc_short}</span>
                          )}
                          {vl.address1 && (
                            <span className="text-xs text-muted-foreground ml-2">({vl.address1})</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenVenueLocationDialog(vl)}
                            data-testid={`edit-vl-${vl.id}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedVenueLocation(vl);
                              setDeleteVenueLocationDialogOpen(true);
                            }}
                            data-testid={`delete-vl-${vl.id}`}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {expanded && locationCount === 0 && (
                  <div className="px-4 py-3 pl-14 text-sm text-muted-foreground italic border-t border-border">
                    No locations defined
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Venue Create/Edit Dialog */}
      <Dialog open={venueDialogOpen} onOpenChange={setVenueDialogOpen}>
        <DialogContent className="max-w-md" data-testid="venue-dialog">
          <DialogHeader>
            <DialogTitle>{selectedVenue ? "Edit Venue" : "New Venue"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
            {/* Client Selection - HIDDEN from UI per stabilization requirements
                Note: client_id still exists in backend model, just not shown in form
            */}
            
            {/* Venue Name */}
            <div className="space-y-2">
              <Label htmlFor="venue-name">Venue Name *</Label>
              <Input
                id="venue-name"
                value={venueFormData.name}
                onChange={(e) => setVenueFormData({ ...venueFormData, name: e.target.value })}
                placeholder="e.g., ExCeL London"
                data-testid="venue-name-input"
              />
            </div>
            
            {/* Address Fields - Clean Layout */}
            <div className="space-y-2">
              <Label htmlFor="venue-address">Address Line 1 *</Label>
              <Input
                id="venue-address"
                value={venueFormData.address}
                onChange={(e) => setVenueFormData({ ...venueFormData, address: e.target.value })}
                placeholder="Street address"
                data-testid="venue-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="venue-address-2">Address Line 2</Label>
              <Input
                id="venue-address-2"
                value={venueFormData.address_line_2}
                onChange={(e) => setVenueFormData({ ...venueFormData, address_line_2: e.target.value })}
                placeholder="Building, floor, etc."
                data-testid="venue-address-2-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="venue-address-3">Address Line 3</Label>
              <Input
                id="venue-address-3"
                value={venueFormData.address_line_3}
                onChange={(e) => setVenueFormData({ ...venueFormData, address_line_3: e.target.value })}
                placeholder="District, area, etc."
                data-testid="venue-address-3-input"
              />
            </div>
            
            {/* City + Postcode Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="venue-city">City</Label>
                <Input
                  id="venue-city"
                  value={venueFormData.city}
                  onChange={(e) => setVenueFormData({ ...venueFormData, city: e.target.value })}
                  placeholder="City"
                  data-testid="venue-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="venue-postcode">Postcode</Label>
                <Input
                  id="venue-postcode"
                  value={venueFormData.postcode}
                  onChange={(e) => setVenueFormData({ ...venueFormData, postcode: e.target.value })}
                  placeholder="Postcode"
                  data-testid="venue-postcode-input"
                />
              </div>
            </div>
            
            {/* Country */}
            <div className="space-y-2">
              <Label htmlFor="venue-country">Country</Label>
              <CountrySelect
                value={venueFormData.country}
                onValueChange={(value) => setVenueFormData({ ...venueFormData, country: value })}
                placeholder="Select country..."
                testId="venue-country-select"
              />
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="venue-notes">Notes</Label>
              <Textarea
                id="venue-notes"
                value={venueFormData.notes}
                onChange={(e) => setVenueFormData({ ...venueFormData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
                data-testid="venue-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVenueDialogOpen(false)} data-testid="venue-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleVenueSave} data-testid="venue-save-btn">
              {selectedVenue ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Venue Location Create/Edit Dialog */}
      <Dialog open={venueLocationDialogOpen} onOpenChange={setVenueLocationDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="venue-location-dialog">
          <DialogHeader>
            <DialogTitle>{selectedVenueLocation ? "Edit Location" : "New Location"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label htmlFor="vl-venue">Venue *</Label>
              <Select
                value={venueLocationFormData.venue_id}
                onValueChange={handleVenueSelectionChange}
                disabled={!!selectedVenueLocation}
              >
                <SelectTrigger id="vl-venue" data-testid="vl-venue-select">
                  <SelectValue placeholder="Select venue" />
                </SelectTrigger>
                <SelectContent>
                  {venues.map((venue) => (
                    <SelectItem key={venue.id} value={venue.id}>
                      {venue.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="vl-name">Location Name *</Label>
              <Input
                id="vl-name"
                value={venueLocationFormData.name}
                onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, name: e.target.value })}
                placeholder="e.g., North Entrance, Loading Bay 3"
                data-testid="vl-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vl-desc-short">Short Description</Label>
              <Input
                id="vl-desc-short"
                value={venueLocationFormData.desc_short}
                onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, desc_short: e.target.value })}
                placeholder="Brief description"
                data-testid="vl-desc-short-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vl-address1">Address Line 1</Label>
              <Input
                id="vl-address1"
                value={venueLocationFormData.address1}
                onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, address1: e.target.value })}
                placeholder="Specific address or directions"
                data-testid="vl-address1-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vl-address2">Address Line 2</Label>
              <Input
                id="vl-address2"
                value={venueLocationFormData.address2}
                onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, address2: e.target.value })}
                placeholder="Additional address info"
                data-testid="vl-address2-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vl-city">City</Label>
                <Input
                  id="vl-city"
                  value={venueLocationFormData.city}
                  onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, city: e.target.value })}
                  placeholder="City"
                  data-testid="vl-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vl-postcode">Postcode</Label>
                <Input
                  id="vl-postcode"
                  value={venueLocationFormData.postcode}
                  onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, postcode: e.target.value })}
                  placeholder="Postcode"
                  data-testid="vl-postcode-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="vl-country">Country</Label>
              <CountrySelect
                value={venueLocationFormData.country}
                onValueChange={(value) => setVenueLocationFormData({ ...venueLocationFormData, country: value })}
                placeholder="Select country..."
                testId="vl-country-select"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vl-latitude">Latitude</Label>
                <Input
                  id="vl-latitude"
                  type="number"
                  step="any"
                  value={venueLocationFormData.latitude}
                  onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, latitude: e.target.value })}
                  placeholder="e.g., 51.5074"
                  data-testid="vl-latitude-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vl-longitude">Longitude</Label>
                <Input
                  id="vl-longitude"
                  type="number"
                  step="any"
                  value={venueLocationFormData.longitude}
                  onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, longitude: e.target.value })}
                  placeholder="e.g., -0.1278"
                  data-testid="vl-longitude-input"
                />
              </div>
            </div>
            <div className="pt-2 border-t">
              <p className="text-sm font-medium text-muted-foreground mb-2">Contact Information</p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="vl-contact-name">Contact Name</Label>
                  <Input
                    id="vl-contact-name"
                    value={venueLocationFormData.contact_name1}
                    onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, contact_name1: e.target.value })}
                    placeholder="Contact person name"
                    data-testid="vl-contact-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vl-contact-phone">Phone</Label>
                    <Input
                      id="vl-contact-phone"
                      value={venueLocationFormData.contact_phone1}
                      onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, contact_phone1: e.target.value })}
                      placeholder="Phone number"
                      data-testid="vl-contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vl-contact-email">Email</Label>
                    <Input
                      id="vl-contact-email"
                      type="email"
                      value={venueLocationFormData.contact_email1}
                      onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, contact_email1: e.target.value })}
                      placeholder="Email address"
                      data-testid="vl-contact-email-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="vl-description">Full Description</Label>
              <Textarea
                id="vl-description"
                value={venueLocationFormData.description}
                onChange={(e) => setVenueLocationFormData({ ...venueLocationFormData, description: e.target.value })}
                placeholder="Detailed description or instructions..."
                rows={3}
                data-testid="vl-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVenueLocationDialogOpen(false)} data-testid="vl-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleVenueLocationSave} data-testid="vl-save-btn">
              {selectedVenueLocation ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Venue Confirmation */}
      <AlertDialog open={deleteVenueDialogOpen} onOpenChange={setDeleteVenueDialogOpen}>
        <AlertDialogContent data-testid="delete-venue-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Venue</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedVenue?.name}"? This will also delete all locations associated with this venue. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-venue-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteVenueConfirm} className="bg-destructive text-destructive-foreground" data-testid="delete-venue-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Venue Location Confirmation */}
      <AlertDialog open={deleteVenueLocationDialogOpen} onOpenChange={setDeleteVenueLocationDialogOpen}>
        <AlertDialogContent data-testid="delete-vl-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Location</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedVenueLocation?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-vl-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteVenueLocationConfirm} className="bg-destructive text-destructive-foreground" data-testid="delete-vl-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* EVENTSFLO-ADMIN-VENUE-SHARE-COPY-UI-V1 */}
      <VenueShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        venue={selectedVenue}
        clients={clients}
        onSaved={(updated) => {
          // Refresh the affected row inline with server response.
          setVenues((prev) =>
            prev.map((v) =>
              v.id === selectedVenue?.id
                ? {
                    ...v,
                    shared_client_ids: updated.shared_client_ids,
                    visibility_type: updated.visibility_type,
                  }
                : v,
            ),
          );
        }}
      />
      <VenueCopyToClientDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        venue={selectedVenue}
        clients={clients}
        locations={(venueLocations || []).filter(
          (l) => l.venue_id === selectedVenue?.id && l.is_active !== false,
        )}
        onCopied={(payload) => {
          // Append the new copy to the list so the admin sees it
          // immediately with the right badge.
          if (payload?.venue) {
            setVenues((prev) => [...prev, payload.venue]);
          }
          if (payload?.locations?.length) {
            setVenueLocations((prev) => [...prev, ...payload.locations]);
          }
        }}
      />

      {/* Import Confirmation Dialog */}
      <AlertDialog open={importConfirmOpen} onOpenChange={setImportConfirmOpen}>
        <AlertDialogContent data-testid="import-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Confirm Import
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="text-destructive font-medium">
                This will permanently delete ALL existing Venues and Locations before importing new data.
              </p>
              <p className="text-sm">Importing from:</p>
              <p className="font-mono text-sm bg-surface-elevated px-2 py-1 rounded">{selectedFile?.name}</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleImportCancel} data-testid="import-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleImportConfirm} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="import-confirm-btn"
            >
              Delete All & Import
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Result Dialog */}
      <Dialog open={importResultOpen} onOpenChange={setImportResultOpen}>
        <DialogContent className="max-w-md" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {/* Status logic: green (no errors), amber (has errors but completed), red (failed) */}
              {importResult?.success && (!importResult?.errors || importResult.errors.length === 0) ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Import Successful
                </>
              ) : importResult?.success && importResult?.errors?.length > 0 ? (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  Import Completed with Warnings
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-red-500" />
                  Import Failed
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {importResult && (
            <div className="space-y-4">
              {/* Summary stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-surface-elevated/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-primary">{importResult.venues_created}</p>
                  <p className="text-sm text-muted-foreground">Venues Created</p>
                </div>
                <div className="bg-surface-elevated/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-primary">{importResult.locations_created}</p>
                  <p className="text-sm text-muted-foreground">Locations Created</p>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Rows processed: {importResult.rows_processed}
              </p>
              
              {/* Warnings */}
              {importResult.warnings?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-amber-500">Warnings:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 max-h-32 overflow-y-auto">
                    {importResult.warnings.map((w, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span className="text-amber-500">•</span> {w}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Errors */}
              {importResult.errors?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-red-500">Errors:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 max-h-32 overflow-y-auto">
                    {importResult.errors.map((e, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span className="text-red-500">•</span> {e}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setImportResultOpen(false)} data-testid="import-result-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
